/**
 * BasisspartenSection - the Basissparten-Checkliste (Soll, Ausschreibung Ch 4): a table with the
 * catalog Soll (Pflicht/Empfehlung + Richtwert), a per-row Ampel (deriveBasisspartenAmpel), the
 * broker's per-customer assessment (relevant? / Override / Notiz, PUT on change), and links to
 * this customer's policies in the Sparte.
 *
 * Behind the `kunden.risikoprofil.basissparten` Kachel visibility key.
 */

import {
  BASISSPARTEN_STATUS_LABELS,
  BASISSPARTE_STATUS_OVERRIDE_VALUES,
  BASISSPARTE_STATUS_OVERRIDE_LABELS,
} from '@brokly/shared';
import type { BasisspartenItem, BasisspartePutBody } from '@brokly/shared';
import { StatusDot } from '../../../../components/ui/StatusDot';
import { EntityLink } from '../../../../components/ui/EntityLink';
import { AMPEL_TONE, AMPEL_LABEL } from './ampel';

export function BasisspartenSection({
  items,
  canWrite,
  onPut,
  onOpenPolicy,
}: {
  items: BasisspartenItem[];
  canWrite: boolean;
  onPut: (sparteKey: string, body: BasisspartePutBody) => void;
  onOpenPolicy?: (policyId: string) => void;
}) {
  return (
    <section aria-labelledby="rp-basissparten" className="rounded-lg border bg-card p-4" data-testid="rp-basissparten">
      <h3 id="rp-basissparten" className="text-sm font-semibold text-foreground">
        Basissparten-Checkliste (Soll)
      </h3>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th className="py-2 pr-3 font-medium">Sparte</th>
              <th className="py-2 pr-3 font-medium">Soll</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 font-medium">Beurteilung</th>
              <th className="py-2 pr-3 font-medium">Policen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.sparte_key} className="border-b last:border-0 align-top" data-testid={`bss-row-${item.sparte_key}`}>
                <td className="py-2 pr-3">
                  <div className="font-medium text-foreground">{item.label}</div>
                  <div className="text-xs text-muted-foreground">{item.richtwert}</div>
                </td>
                <td className="py-2 pr-3">
                  <span className="text-xs text-foreground">{BASISSPARTEN_STATUS_LABELS[item.default_status]}</span>
                  <div className="text-xs text-muted-foreground">{item.kriterium}</div>
                </td>
                <td className="py-2 pr-3">
                  <StatusDot tone={AMPEL_TONE[item.ampel]} label={AMPEL_LABEL[item.ampel]} />
                </td>
                <td className="py-2 pr-3">
                  <select
                    disabled={!canWrite}
                    value={item.status_override ?? ''}
                    onChange={(e) =>
                      onPut(item.sparte_key, {
                        status_override: (e.target.value || null) as never,
                      })
                    }
                    className="rounded-md border bg-background px-2 py-1 text-xs"
                    data-testid={`bss-override-${item.sparte_key}`}
                    aria-label={`Beurteilung ${item.label}`}
                  >
                    <option value="">Offen</option>
                    {BASISSPARTE_STATUS_OVERRIDE_VALUES.map((v) => (
                      <option key={v} value={v}>
                        {BASISSPARTE_STATUS_OVERRIDE_LABELS[v]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  {item.policy_ids.length === 0 ? (
                    <span className="text-xs text-muted-foreground">-</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {item.policy_ids.map((pid) => (
                        <EntityLink key={pid} onActivate={onOpenPolicy ? () => onOpenPolicy(pid) : undefined}>
                          Police
                        </EntityLink>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
